import { Create } from '@refinedev/mui'
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material'
import { useForm } from '@refinedev/react-hook-form'
import { Controller } from 'react-hook-form'
import { useGetIdentity, useList } from '@refinedev/core'
import { User } from '@/utils/auth/auth0'
import { useNavigate } from 'react-router-dom'
import { useMemo } from 'react'
import { IwiData } from '@/interfaces/iwi'
import { getAsciiName } from '../../utils/textUtils'
import { EMAIL_REGEX } from '@/utils/validation'

interface FunderFormValues {
  name: string
  type: string
  website: string
  description: string
  notes: string
  status: string
  active: boolean
  only_funds_registered_charities: boolean
  gambling_proceeds: boolean
  is_international: boolean
  iwi: number[]
  email_address: string
}

export const FunderCreate = () => {
  const navigate = useNavigate()
  const { data: user } = useGetIdentity() as User
  const {
    saveButtonProps,
    refineCore: { formLoading, onFinish },
    register,
    formState: { errors },
    getValues,
    watch,
    control,
  } = useForm({
    warnWhenUnsavedChanges: true,
    refineCoreProps: {
      redirect: false,
    },
    defaultValues: {
      active: true,
      status: '',
      notes: '',
      description: '',
      website: '',
      type: '',
      name: '',
      only_funds_registered_charities: false,
      gambling_proceeds: false,
      is_international: false,
      iwi: [] as number[],
      email_address: '',
    },
  })

  const iwiValue = watch('iwi') || []

  const { data: iwiData, isLoading: iwiDataIsLoading } = useList({
    resource: 'iwi',
    config: {
      pagination: { current: 1, pageSize: 200 },
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting iwi list',
        description:
          error?.message || 'An error occurred while fetching iwi list',
        type: 'error',
      }
    },
  })

  const iwiList = useMemo(
    () => (iwiData?.data as IwiData[]) || [],
    [iwiData?.data]
  )

  const selectedIwi = useMemo(() => {
    if (!iwiValue || !Array.isArray(iwiValue)) return []
    return iwiValue
      .map((iwiId: number) => iwiList.find((iwi) => iwi.id === iwiId))
      .filter((iwi): iwi is IwiData => iwi !== undefined)
  }, [iwiValue, iwiList])

  async function handleSubmit(values: FunderFormValues) {
    try {
      if (!values.name) {
        alert('Name is required')
        return
      }
      const updatedValues = {
        ...values,
        meta: {
          updated_by:
            user?.name ||
            `${user?.given_name || ''} ${user?.family_name || ''}`,
        },
      }

      const response = await onFinish(updatedValues)

      if (response?.data?.uuid) {
        navigate(`/funders/${response.data.uuid}`)
      }
    } catch (error) {
      alert('Error creating funder. Please try again.')
    }
  }

  return (
    <Create
      isLoading={formLoading}
      saveButtonProps={{
        ...saveButtonProps,
        onClick: () => handleSubmit(getValues() as FunderFormValues),
      }}
    >
      <Box
        component="form"
        sx={{ display: 'flex', flexDirection: 'column' }}
        autoComplete="off"
      >
        <TextField
          {...register('name', {
            required: {
              value: true,
              message: 'This field is required',
            },
            minLength: 1,
          })}
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Name"
          name="name"
        />
        <FormControlLabel
          control={<Checkbox {...register('active')} name="active" />}
          label="Active"
        />
        {!watch('active') && (
          <TextField
            {...register('status')}
            defaultValue={''}
            error={!!(errors as any)?.status}
            helperText={(errors as any)?.status?.message}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Status (optional, as a description of why the funder is inactive)"
            name="status"
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              {...register('only_funds_registered_charities')}
              name="only_funds_registered_charities"
            />
          }
          label="Only funds registered charities"
        />
        <FormControlLabel
          control={
            <Checkbox
              {...register('gambling_proceeds')}
              name="gambling_proceeds"
            />
          }
          label="Gambling Proceeds"
        />
        <FormControlLabel
          control={
            <Checkbox
              {...register('is_international')}
              name="is_international"
            />
          }
          label="Is International"
        />

        <Controller
          control={control}
          name="iwi"
          defaultValue={[]}
          render={({ field }) => (
            <Autocomplete
              multiple
              value={selectedIwi}
              sx={{ my: 3 }}
              onChange={(_event, newValues) => {
                const newIwi = newValues.map((value) => value.id)
                field.onChange(newIwi)
              }}
              options={iwiList}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, params) => {
                const inputValue = params.inputValue?.toLowerCase().trim()
                if (!inputValue) return []
                const asciiInput = getAsciiName(inputValue)

                return options.filter((option) => {
                  return getAsciiName(option.name).includes(asciiInput)
                })
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  label="Iwi"
                  {...params}
                  placeholder="Search iwi"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: { backgroundColor: '#fff' },
                  }}
                />
              )}
            />
          )}
        />

        <TextField
          {...register('type', {
            required: 'This field is required',
          })}
          error={!!(errors as any)?.type}
          helperText={(errors as any)?.type?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Type"
          name="type"
        />
        <TextField
          {...register('website', {
            required: 'This field is required',
          })}
          error={!!(errors as any)?.website}
          helperText={(errors as any)?.website?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="url"
          label="Website"
          name="website"
        />
        <TextField
          {...register('email_address', {
            setValueAs: (v: string) => v?.trim() || null,
            validate: (v) =>
              !v || EMAIL_REGEX.test(v) || 'Must be a valid email address',
          })}
          error={!!(errors as any)?.email_address}
          helperText={(errors as any)?.email_address?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="email"
          label="Email Address"
          name="email_address"
        />
        <TextField
          {...register('description', {
            required: 'This field is required',
          })}
          error={!!(errors as any)?.description}
          helperText={(errors as any)?.description?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          multiline
          label="Description"
          name="description"
        />
        <TextField
          {...register('notes')}
          error={!!(errors as any)?.notes}
          helperText={(errors as any)?.notes?.message}
          margin="normal"
          fullWidth
          multiline
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Notes"
          name="notes"
        />
      </Box>
    </Create>
  )
}
